import { ReactRenderer } from '@tiptap/react';
import tippy, { Instance, Props } from 'tippy.js';
import { Editor, Range } from '@tiptap/core';
import CommandsList, { CommandsListHandle } from './commandList';

type CommandProps = {
  editor: Editor;
  range: Range;
  props: {
    command: (params: { editor: Editor; range: Range }) => void;
  };
};
interface SlashSuggestionProps {
  recordSnap?: () => void;
  handleFileInput?: () => void;
  openGifModal?: () => void;
}

interface CommandItem {
  title: string;
  iconName: string;
  command: (item: CommandItem) => void;
}

interface RenderProps {
  editor: Editor;
  clientRect?: () => DOMRect;
  command: (item: CommandItem) => void;
}

interface UpdateProps {
  query: string;
}

interface KeyDownProps {
  event: KeyboardEvent;
}

const slashSuggestion = (props: SlashSuggestionProps) => {
  const { recordSnap = () => {}, handleFileInput = () => {}, openGifModal = () => {} } = props;

  return {
    char: '/',
    command: ({ editor, range, props: { command } }: CommandProps) => {
      // This command is called when an item is selected from the suggestion list
      // Execute the command passed by the Commands extension
      command({ editor, range });
    },
    items: ({ query }: UpdateProps) =>
      [
        {
          iconName: 'heading1-icon',
          title: 'Heading 1',
          command: ({ editor, range }: { editor: Editor; range: Range }) => {
            editor
              .chain()
              .focus()
              .deleteRange({ from: range.from, to: range.to + query.length })
              .setNode('heading', { level: 1 })
              .run();
          }
        },
        {
          iconName: 'heading2-icon',
          title: 'Heading 2',
          command: ({ editor, range }: { editor: Editor; range: Range }) => {
            editor
              .chain()
              .focus()
              .deleteRange({ from: range.from, to: range.to + query.length })
              .setNode('heading', { level: 2 })
              .run();
          }
        },
        {
          iconName: 'heading3-icon',
          title: 'Heading 3',
          command: ({ editor, range }: { editor: Editor; range: Range }) => {
            editor
              .chain()
              .focus()
              .deleteRange({ from: range.from, to: range.to + query.length })
              .setNode('heading', { level: 3 })
              .run();
          }
        },
        {
          iconName: 'bulleted-list-icon',
          title: 'Bulleted list',
          command: ({ editor, range }: { editor: Editor; range: Range }) => {
            editor
              .chain()
              .focus()
              .deleteRange({ from: range.from, to: range.to + query.length })
              .toggleBulletList()
              .run();
          }
        },
        {
          iconName: 'number-list-icon',
          title: 'Number List',
          command: ({ editor, range }: { editor: Editor; range: Range }) => {
            editor
              .chain()
              .focus()
              .deleteRange({ from: range.from, to: range.to + query.length })
              .toggleOrderedList()
              .run();
          }
        },
        {
          iconName: 'check-list-icon',
          title: 'Checklist',
          command: ({ editor, range }: { editor: Editor; range: Range }) => {
            editor
              .chain()
              .focus()
              .deleteRange({ from: range.from, to: range.to + query.length })
              .toggleTaskList()
              .run();
          }
        },
        {
          iconName: 'file-attachment-icon',
          title: 'Attach File',
          command: ({ editor, range }: { editor: Editor; range: Range }) => {
            editor
              .chain()
              .focus()
              .deleteRange({ from: range.from, to: range.to + query.length })
              .run();
            handleFileInput();
          }
        },
        {
          iconName: 'insert-gif-icon',
          title: 'Insert GIF',
          command: ({ editor, range }: { editor: Editor; range: Range }) => {
            editor
              .chain()
              .focus()
              .deleteRange({ from: range.from, to: range.to + query.length })
              .run();
            openGifModal();
          }
        },
        {
          iconName: 'record-snap-icon',
          title: 'Record Snap',
          command: ({ editor, range }: { editor: Editor; range: Range }) => {
            editor
              .chain()
              .focus()
              .deleteRange({ from: range.from, to: range.to + query.length })
              .run();
            recordSnap();
          }
        },
        {
          iconName: 'html-code-icon',
          title: 'Code Block',
          command: ({ editor, range }: { editor: Editor; range: Range }) => {
            editor
              .chain()
              .focus()
              .deleteRange({ from: range.from, to: range.to + query.length })
              .toggleCodeBlock()
              .run();
          }
        },
        {
          iconName: 'divider-icon',
          title: 'Divider',
          command: ({ editor, range }: { editor: Editor; range: Range }) => {
            editor
              .chain()
              .focus()
              .deleteRange({ from: range.from, to: range.to + query.length })
              .insertContent('<hr /><p></p>')
              .run();
          }
        },
        {
          iconName: 'block-quote-icon',
          title: 'Block Quote',
          command: ({ editor, range }: { editor: Editor; range: Range }) => {
            editor
              .chain()
              .focus()
              .deleteRange({ from: range.from, to: range.to + query.length })
              .toggleBlockquote()
              .run();
          }
        },
        {
          iconName: 'table-icon',
          title: 'Table',
          command: ({ editor, range }: { editor: Editor; range: Range }) => {
            // Calculate insertion position BEFORE running chain
            const insertPos = range.from;
            editor
              .chain()
              .focus()
              .deleteRange({ from: range.from, to: range.to + query.length })
              .insertContent([
                {
                  type: 'table',
                  content: [
                    {
                      type: 'tableRow',
                      content: [
                        { type: 'tableHeader', content: [{ type: 'paragraph' }] },
                        { type: 'tableHeader', content: [{ type: 'paragraph' }] },
                        { type: 'tableHeader', content: [{ type: 'paragraph' }] }
                      ]
                    },
                    {
                      type: 'tableRow',
                      content: [
                        { type: 'tableCell', content: [{ type: 'paragraph' }] },
                        { type: 'tableCell', content: [{ type: 'paragraph' }] },
                        { type: 'tableCell', content: [{ type: 'paragraph' }] }
                      ]
                    },
                    {
                      type: 'tableRow',
                      content: [
                        { type: 'tableCell', content: [{ type: 'paragraph' }] },
                        { type: 'tableCell', content: [{ type: 'paragraph' }] },
                        { type: 'tableCell', content: [{ type: 'paragraph' }] }
                      ]
                    }
                  ]
                },
                { type: 'paragraph' }
              ])
              .run();

            // Directly move cursor inside first cell (no traversal, no timeout)
            editor.commands.setTextSelection(insertPos + 3);
            editor.commands.focus();
          }
        },
        {
          iconName: 'toggle-icon',
          title: 'Toggle',
          command: ({ editor, range }: { editor: Editor; range: Range }) => {
            editor
              .chain()
              .focus()
              .deleteRange({ from: range.from, to: range.to + query.length })
              .setDetails()
              .run();
          }
        }
      ].filter((item) => item.title.toLowerCase().includes(query.toLowerCase())),

    render: () => {
      let component: ReactRenderer<CommandsListHandle> | null = null;
      let popup: Instance<Props>[] | null = null;

      return {
        onStart: (props: RenderProps) => {
          const { editor, clientRect, command } = props || {};
          if (!editor || !clientRect || !command) return;

          component = new ReactRenderer(CommandsList, {
            editor,
            props: {
              items: slashSuggestion({ recordSnap, openGifModal, handleFileInput }).items({ query: '' }),
              command
            }
          });

          popup = tippy('body', {
            getReferenceClientRect: clientRect,
            appendTo: () => document.body,
            content: component.element,
            showOnCreate: true,
            interactive: true,
            trigger: 'manual',
            placement: 'bottom-start'
          });
        },

        onUpdate: (props: UpdateProps) => {
          if (component) {
            component.updateProps({
              items: slashSuggestion({ recordSnap, openGifModal, handleFileInput }).items(props)
            });
          }
        },

        onKeyDown: (props: KeyDownProps) => {
          return component?.ref?.onKeyDown(props.event) ?? false;
        },

        onExit: () => {
          popup?.[0]?.destroy();
          component?.destroy();
          popup = null;
          component = null;
        }
      };
    }
  };
};

export default slashSuggestion;
